import { ListStyle } from '@yleisradio/areena-types';
import { Consent } from 'types/yleConsentSdk';
import { snowplowAnalyticsProperties } from '../properties/frontend';
import {
  attributeContextGenerator,
  clickEventGenerator,
  viewEventGenerator,
  elementContextGenerator,
  entityContextGenerator,
  GenericContext,
  groupContextGeneratorForContentCard,
  groupContextGeneratorForHighlightCard,
  groupContextGeneratorForList,
  groupContextGeneratorForVideoFeed,
  imageContextGenerator,
  wrapperContextGenerator,
  yleConsentGenerator,
  recommendationContextGenerator,
  userProfileGenerator,
  areenaPackageContextGenerator,
  abTestContextGenerator,
  ViewModeType,
  tabContextGenerator,
  contentCardContextGenerator,
  uriContextGenerator,
  searchContextGenerator,
  reactionEventGenerator,
  seriesContextGenerator,
  programContextGenerator,
} from './contextGenerators';
import { GroupEngagementData } from 'components/SnowplowEngagementAnalytics/types';
import { envVariables } from 'utils/environment';

export const initSnowplowTracker = (): void => {
  const { versionNumber } = envVariables;
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const { appId, collectorUrl, mediaPluginUrl, trackerNamespace } =
    snowplowAnalyticsProperties;

  window.yleSnowplow('newTracker', trackerNamespace, collectorUrl, {
    appId: appId,
    appVersion: versionNumber,
    bufferSize: 1,
    keepalive: true,
    discoverRootDomain: true,
    cookieSameSite: 'Lax',
    contexts: {
      browser: true,
      session: true,
      webPage: true,
    },
    maxLocalStorageQueueSize: 100, // Decreased from 1000 to avoid QuotaExceededError
    platform: 'web',
  });

  window.yleSnowplow('addPlugin', mediaPluginUrl, [
    'snowplowMedia',
    'SnowplowMediaPlugin',
  ]);
};

const setActiveEntityContext = (context: GenericContext | null) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  if (context) {
    window.yleSnowplow('addGlobalContexts', {
      active_entity: context,
    });
  } else {
    window.yleSnowplow('removeGlobalContexts', ['active_entity']);
  }
};

const setYleContext = (context: GenericContext) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('addGlobalContexts', {
    yle_context: context,
  });
};

export const addConsentContext = (consent: Consent) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('addGlobalContexts', {
    consent: yleConsentGenerator(consent),
  });
};

export const addUserProfile = (age: number) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('addGlobalContexts', {
    user_profile: userProfileGenerator(age),
  });
};

export const clearUser = () => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('clearUserData');
  window.yleSnowplow('newSession');
};

export const setUser = (transientId: string) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('setUserId', transientId);
};

export const trackPageView = (
  yleContext: GenericContext,
  activeEntity: GenericContext | null
) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  setYleContext(yleContext);
  setActiveEntityContext(activeEntity);

  window.yleSnowplow('trackPageView');
};

export const trackCardView = ({
  entityId,
  cardIndex,
  cardTitle,
  cardViewUuid,
  imageId,
  packageId,
  viewMode,
  wrapperLabel,
  wrapperType,
  groupData,
  searchQuery,
  searchIdentifier,
}: TrackCardViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(cardViewUuid, 'entity', viewMode);

  const listIndex = groupData?.index;
  const listStyle = groupData?.style;

  const recommendation = groupData?.recommendation;
  const abTest = groupData?.abTest;

  const context = [
    entityContextGenerator(entityId),
    wrapperContextGenerator({ type: wrapperType, label: wrapperLabel }),
    groupContextGeneratorForList(groupData),
    tabContextGenerator(groupData?.tabTitle),
    elementContextGenerator({
      elementIndex: cardIndex,
      groupIndex: listIndex,
      listStyle,
      title: cardTitle,
      type: 'card',
    }),
    ...(imageId ? [imageContextGenerator(imageId)] : []),
    ...(recommendation?.id && recommendation?.method
      ? [
          recommendationContextGenerator(
            recommendation.id,
            recommendation.method
          ),
        ]
      : []),
    ...(packageId
      ? [areenaPackageContextGenerator(packageId, 'engagement_view_source')]
      : []),
    ...(abTest?.name && abTest?.group
      ? [abTestContextGenerator(abTest.name, abTest.group)]
      : []),
    ...(searchIdentifier
      ? [
          searchContextGenerator(
            searchIdentifier,
            searchQuery,
            'engagement_view_source'
          ),
        ]
      : []),
  ].filter(Boolean);

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackHighlightCardView = ({
  viewUuid,
  contentIndex,
  imageId,
  title,
  entityId,
  targetUri,
}: TrackHighlightCardViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const viewMode: ViewModeType = 'thumbnail';
  const viewEntityType = entityId ? 'entity' : 'uri';

  const event = viewEventGenerator(
    viewUuid,
    'entity',
    viewMode,
    viewEntityType
  );

  const context = [
    wrapperContextGenerator({}),
    entityId
      ? entityContextGenerator(entityId)
      : targetUri
        ? uriContextGenerator(targetUri)
        : null,
    groupContextGeneratorForHighlightCard(title),
    elementContextGenerator({
      elementIndex: 0,
      groupIndex: contentIndex,
      listStyle: undefined,
      title,
      type: 'highlight_card',
    }),
    ...(imageId ? [imageContextGenerator(imageId)] : []),
  ].filter(Boolean);

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackContentCardView = ({
  viewUuid,
  contentCardId,
  placementId,
  targetUri,
}: TrackContentCardViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'entity', 'thumbnail', 'uri');

  const context = [
    wrapperContextGenerator({}),
    contentCardContextGenerator(contentCardId, placementId),
    uriContextGenerator(targetUri),
    groupContextGeneratorForContentCard(),
  ].filter(Boolean);

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackPlayQueueView = (viewUuid: string) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'ui_element', 'overlay');
  const context = [
    wrapperContextGenerator({ label: 'playqueue', type: 'overlay' }),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackPlayQueueItemView = (viewUuid: string, entityId: string) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'entity', 'thumbnail');
  const context = [
    wrapperContextGenerator({ label: 'playqueue', type: 'overlay' }),
    entityContextGenerator(entityId),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackClick = ({ action, viewUuid }: TrackClickArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = clickEventGenerator(action, viewUuid);

  window.yleSnowplow('trackSelfDescribingEvent', { event });
};

const contextMenuWrapperContext = () =>
  wrapperContextGenerator({ type: 'overlay', label: 'card_menu' });

export const trackContextMenuView = ({
  viewUuid,
  entityId,
}: {
  viewUuid: string;
  entityId: string;
}) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'ui_element', 'overlay');
  const context = [
    contextMenuWrapperContext(),
    entityContextGenerator(entityId, 'engagement_view_source'),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackContextMenuClick = ({
  action,
  title,
  cardViewUuid,
}: {
  action: 'link' | 'play';
  title: 'continue' | 'play_from_beginning' | 'to_content_page';
  cardViewUuid: string;
}) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = clickEventGenerator(action, cardViewUuid);
  const context = [
    contextMenuWrapperContext(),
    elementContextGenerator({
      type: 'button',
      title,
      elementIndex: undefined,
      groupIndex: undefined,
      listStyle: undefined,
    }),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

// Other than play and link clicks are attached to the menu's view
export const trackContextMenuSelect = ({
  attribute,
  menuViewUuid,
}: {
  attribute:
    | 'add_to_favorites'
    | 'remove_from_favorites'
    | 'delete_from_list'
    | 'mark_as_watched';
  menuViewUuid: string;
}) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = clickEventGenerator('select', menuViewUuid);
  const context = [
    contextMenuWrapperContext(),
    attributeContextGenerator(attribute, 'context_menu'),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackContextMenuReaction = ({
  name,
  undo,
  entityId,
  pointerType,
}: {
  name: 'thumbsup' | 'thumbsdown';
  undo: boolean;
  entityId: string;
  pointerType: string | undefined;
}) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const predicate = 'engagement_reaction_target';
  const targetContext =
    pointerType === 'series'
      ? seriesContextGenerator(entityId, predicate)
      : pointerType === 'program'
        ? programContextGenerator(entityId, predicate)
        : entityContextGenerator(entityId, predicate);

  const event = reactionEventGenerator(name, undo);
  const context = [contextMenuWrapperContext(), targetContext];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackVideoFeedItemView = ({
  entityId,
  viewUuid,
  itemIndex,
  contentIndex,
  label,
  recommendationId,
  recommendationMethod,
  tests,
}: TrackVideoFeedItemViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'entity', 'thumbnail');

  const context = [
    entityContextGenerator(entityId),
    wrapperContextGenerator({}),
    groupContextGeneratorForVideoFeed(label),
    elementContextGenerator({
      elementIndex: itemIndex,
      groupIndex: contentIndex,
      listStyle: undefined,
      title: undefined,
      type: 'card',
    }),
    ...(recommendationId && recommendationMethod
      ? [recommendationContextGenerator(recommendationId, recommendationMethod)]
      : []),
    ...(tests?.map(({ name, group }) => abTestContextGenerator(name, group)) ??
      []),
  ].filter(Boolean);

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const getListContext = (groupData: GroupEngagementData | undefined) => {
  return [wrapperContextGenerator({}), groupContextGeneratorForList(groupData)];
};

export const flushBuffer = () => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('flushBuffer');
};

export type GetListContextArgs = {
  isPaginated?: boolean;
  label: string | undefined;
  length?: number | null;
  referer: string | undefined;
  style: ListStyle | undefined;
  url: string | undefined;
};

export type TrackCardViewArgs = {
  cardIndex: number;
  cardTitle: string | undefined;
  cardViewUuid: string;
  entityId: string;
  imageId: string | undefined;

  packageId: string | undefined;

  groupData: GroupEngagementData | undefined;
  viewMode: ViewModeType;
  wrapperLabel: string | undefined;
  wrapperType: 'main' | 'overlay' | undefined;
  searchQuery: string | undefined;
  searchIdentifier: string | undefined;
};

export type TrackHighlightCardViewArgs = {
  title: string | undefined;
  viewUuid: string;
  imageId: string | undefined;
  contentIndex: number;
  entityId: string | undefined;
  targetUri: string | undefined;
};

export type TrackClickArgs = {
  action: 'link' | 'play';
  viewUuid: string;
};

export type TrackContentCardViewArgs = {
  viewUuid: string;
  contentCardId: string;
  placementId: string | undefined;
  targetUri: string;
};

export type TrackVideoFeedItemViewArgs = {
  entityId: string;
  viewUuid: string;
  itemIndex: number;
  contentIndex: number;
  label: string | undefined;
  recommendationId?: string | undefined;
  recommendationMethod?: string | undefined;
  tests?: Array<{ name: string; group: string }>;
};
